import {
  useCreateUnderwear,
  useUpdateUnderwear,
} from '@/api/underwear/underwear.hooks';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useZodForm,
} from '@/lib/form';
import {
  schema,
  updateSchema,
  UpdateUnderwearSchemaType,
} from '@/schemas/underwear.schema';
import {
  UnderwearCategory,
  UnderwearCategoryTranslationFR,
  UnderwearResponse,
} from '@repo/types/src/underwear';
import { CirclePlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  underwear?: UnderwearResponse;
}

export const AddUnderwear = ({ underwear }: Props = {}) => {
  const isEdit = underwear !== undefined;
  const createMutation = useCreateUnderwear();
  const updateMutation = useUpdateUnderwear();
  const [open, setOpen] = useState(false);

  const form = useZodForm({
    schema: isEdit ? updateSchema : schema,
    defaultValues: {
      name: underwear?.name ?? '',
      nameEn: underwear?.nameEn ?? '',
      sortOrder: underwear?.sortOrder ?? 0,
      category: underwear?.category ?? 'PANTY',
      isAvailableOnOrpair: underwear?.isAvailableOnOrpair ?? false,
    },
  });

  const handleSubmit = (payload: UpdateUnderwearSchemaType) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: underwear.id, payload },
        {
          onSuccess: (data) => {
            setOpen(false);
            toast.success(
              `La culotte ${data.name} a été modifiée avec succès!`,
              { duration: 3000 },
            );
          },
          onError: () => {
            toast.error(
              `Erreur lors de la modification de la culotte ${payload.name}`,
              { duration: 3000 },
            );
          },
        },
      );
    } else {
      createMutation.mutate(
        { ...payload, file: payload.file! },
        {
          onSuccess: (data) => {
            setOpen(false);
            toast.success(`La culotte ${data.name} a été créée avec succès!`, {
              duration: 3000,
            });
          },
          onError: () => {
            toast.error(
              `Erreur lors de la création de la culotte ${payload.name}`,
              { duration: 3000 },
            );
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon">
            <Pencil size={20} />
          </Button>
        ) : (
          <Button>
            <div className="flex flex-row gap-2 items-center">
              <CirclePlus size={24} />
              <span>Ajouter une culotte</span>
            </div>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <Form
          form={form}
          onSubmit={handleSubmit}
          className="w-full flex flex-col space-y-4"
        >
          <DialogHeader></DialogHeader>
          <DialogTitle className="text-center">
            {isEdit ? 'Modifier' : 'Ajouter'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isEdit
              ? 'Modifier la culotte menstruelle'
              : 'Ajouter une culotte menstruelle'}
          </DialogDescription>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input type="text" placeholder="Nom du modèle" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nameEn"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Nom anglais (optionnel)"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Catégorie</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisissez une catégorie" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(UnderwearCategory).map(([key, value]) => (
                      <SelectItem key={key} value={value}>
                        {UnderwearCategoryTranslationFR[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sortOrder"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ordre d'apparition</FormLabel>
                <FormDescription>
                  Définition de l'ordre d'apparition des culottes dans
                  l'application
                </FormDescription>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Ordre d'apparition"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="file"
            render={({ field: { value: _value, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>Image</FormLabel>
                {isEdit && (
                  <FormDescription>
                    Laisser vide pour conserver l'image actuelle. Format
                    recommandé : PNG (823 x 823 pixels)
                  </FormDescription>
                )}
                {!isEdit && (
                  <FormDescription>
                    Format recommandé : Image PNG (823 x 823 pixels)
                  </FormDescription>
                )}
                <FormControl>
                  <Input
                    type="file"
                    placeholder="Fichier"
                    {...fieldProps}
                    accept="image/png"
                    onChange={(event) =>
                      onChange(event.target.files && event.target.files[0])
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isAvailableOnOrpair"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="!mt-0">Dispo sur Orpair</FormLabel>
              </FormItem>
            )}
          />

          <DialogFooter className="flex gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Annuler</Button>
            </DialogClose>
            <Button variant="default" type="submit">
              {isEdit ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
